import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiX, HiCheck, HiOutlinePhotograph, HiChevronUp, HiChevronDown, HiSearch } from 'react-icons/hi';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Image } from 'primereact/image';
import './styles/SpeakersManagement.css';
import { speakersService } from '../../services/speakersService';
import { cadreConferenceService } from '../../services/cadreConferenceService';
import countries from './countries.json';

const ExpandableText = ({ text, maxChars = 50 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    if (!text) return null;
    if (text.length <= maxChars) return <span>{text}</span>;
    return (
        <div style={{ whiteSpace: 'normal', lineHeight: '1.4', fontSize: '0.9em', maxWidth: '300px' }}>
            {isExpanded ? text : `${text.substring(0, maxChars)}... `}
            <span 
                onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                }}
                style={{ 
                    color: '#002b5b', 
                    cursor: 'pointer', 
                    fontWeight: '600',
                    fontSize: '0.85em',
                    display: 'block',
                    marginTop: '4px'
                }}
            >
                {isExpanded ? 'Voir moins' : 'voir plus'}
            </span>
        </div>
    );
};

function SpeakersManagement({ selectedEdition }) {
  const [speakersData, setSpeakersData] = useState({
    speakersHeader: { title: 'Nos Intervenants' },
    speakersIntro: { text: '' },
    speakersSection: { title: 'Intervenants Confirmés' },
    onlineSpeakersHeader: { title: 'Intervenants En Ligne' },
    onlineSpeakersIntro: { text: '' },
    onlineSpeakersSection: { title: 'Conférenciers Online' },
    displayMode: 'grouped',
    categoryInternationalOrder: 1,
    categoryMarocainOrder: 2,
    speakers: [],
    onlineSpeakers: []
  });

  const [activeTab, setActiveTab] = useState('regular');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [cadresList, setCadresList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (selectedEdition) {
      loadSpeakersData();
      loadCadresList();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(speakersData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [speakersData, originalData]);

  const loadSpeakersData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading speakers data for edition:', selectedEdition);
      const data = await speakersService.getSpeakersData(selectedEdition);
      
      if (!data || ((!data.speakersHeader?.title || data.speakersHeader.title === 'Nos Intervenants') && 
                    (!data.speakersIntro?.text || data.speakersIntro.text === '') &&
                    (!data.speakers || data.speakers.length === 0) &&
                    (!data.onlineSpeakers || data.onlineSpeakers.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          speakersHeader: { title: 'Nos Intervenants' },
          speakersIntro: { text: 'Découvrez nos conférenciers exceptionnels' },
          speakersSection: { title: 'Intervenants Confirmés' },
          onlineSpeakersHeader: { title: 'Intervenants En Ligne' },
          onlineSpeakersIntro: { text: 'Nos conférenciers en ligne' },
          onlineSpeakersSection: { title: 'Conférenciers Online' },
          displayMode: 'grouped',
          categoryInternationalOrder: 1,
          categoryMarocainOrder: 2,
          speakers: [],
          onlineSpeakers: []
        };
        setSpeakersData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setSpeakersData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading speakers data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        speakersHeader: { title: 'Nos Intervenants' },
        speakersIntro: { text: 'Découvrez nos conférenciers exceptionnels' },
        speakersSection: { title: 'Intervenants Confirmés' },
        onlineSpeakersHeader: { title: 'Intervenants En Ligne' },
        onlineSpeakersIntro: { text: 'Nos conférenciers en ligne' },
        onlineSpeakersSection: { title: 'Conférenciers Online' },
        displayMode: 'grouped',
        categoryInternationalOrder: 1,
        categoryMarocainOrder: 2,
        speakers: [],
        onlineSpeakers: []
      };
      setSpeakersData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const loadCadresList = async () => {
    try {
      const cadres = await cadreConferenceService.getCadresData(selectedEdition);
      setCadresList(cadres);
    } catch (e) {
      setCadresList([]);
    }
  };


  const handleEditSpeaker = (speaker) => {
    setSelectedSpeaker(speaker);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteSpeaker = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet intervenant ?')) {
      try {
        await speakersService.deleteSpeaker(id);
        setSpeakersData(prev => ({
          ...prev,
          speakers: prev.speakers.filter(speaker => speaker.id !== id),
          onlineSpeakers: prev.onlineSpeakers.filter(speaker => speaker.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting speaker:', error);
        alert('Erreur lors de la suppression de l\'intervenant.');
      }
    }
  };

  const handleAddSpeaker = () => {
    setSelectedSpeaker(null);
    setModalMode('add');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSpeaker(null);
    setModalMode('add');
  };

  const handleSave = async () => {
    try {
      await speakersService.updateSpeakersPageData(speakersData, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(speakersData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving page data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setSpeakersData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleMoveUp = async (id) => {
    const listType = activeTab === 'regular' ? 'speakers' : 'onlineSpeakers';
    const speakers = [...speakersData[listType]];
    const index = speakers.findIndex(s => s.id === id);
    if (index === -1) return;

    const currentSpeaker = speakers[index];
    const category = currentSpeaker.mainCategory || 'international';

    // Find the previous speaker in the SAME category
    let prevIndex = -1;
    for (let i = index - 1; i >= 0; i--) {
      if ((speakers[i].mainCategory || 'international') === category) {
        prevIndex = i;
        break;
      }
    }

    if (prevIndex === -1) return;

    // Swap them
    [speakers[prevIndex], speakers[index]] = [speakers[index], speakers[prevIndex]];

    // Recalculate all display orders to be safe
    const updatedSpeakers = speakers.map((s, idx) => ({ ...s, displayOrder: idx }));

    setSpeakersData(prev => ({
      ...prev,
      [listType]: updatedSpeakers
    }));

    try {
      // Save ALL speakers order of that list type
      const updatePromises = updatedSpeakers.map((s, idx) => 
        speakersService.updateSpeakerOrder(s.id, idx)
      );
      await Promise.all(updatePromises);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleMoveDown = async (id) => {
    const listType = activeTab === 'regular' ? 'speakers' : 'onlineSpeakers';
    const speakers = [...speakersData[listType]];
    const index = speakers.findIndex(s => s.id === id);
    if (index === -1) return;

    const currentSpeaker = speakers[index];
    const category = currentSpeaker.mainCategory || 'international';

    // Find the next speaker in the SAME category
    let nextIndex = -1;
    for (let i = index + 1; i < speakers.length; i++) {
      if ((speakers[i].mainCategory || 'international') === category) {
        nextIndex = i;
        break;
      }
    }

    if (nextIndex === -1) return;

    // Swap them
    [speakers[index], speakers[nextIndex]] = [speakers[nextIndex], speakers[index]];

    // Recalculate all display orders
    const updatedSpeakers = speakers.map((s, idx) => ({ ...s, displayOrder: idx }));

    setSpeakersData(prev => ({
      ...prev,
      [listType]: updatedSpeakers
    }));

    try {
      // Save ALL speakers order
      const updatePromises = updatedSpeakers.map((s, idx) => 
        speakersService.updateSpeakerOrder(s.id, idx)
      );
      await Promise.all(updatePromises);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleUpdateRegularSpeakersHeader = (field, value) => {
    if (field === 'title') {
      setSpeakersData(prev => ({ 
        ...prev, 
        speakersHeader: { ...prev.speakersHeader, title: value } 
      }));
    } else if (field === 'text') {
      setSpeakersData(prev => ({ 
        ...prev, 
        speakersIntro: { ...prev.speakersIntro, text: value } 
      }));
    } else if (field === 'sectionTitle') {
      setSpeakersData(prev => ({ 
        ...prev, 
        speakersSection: { ...prev.speakersSection, title: value } 
      }));
    }
  };

  const handleUpdateOnlineSpeakersHeader = (field, value) => {
    if (field === 'title') {
      setSpeakersData(prev => ({ 
        ...prev, 
        onlineSpeakersHeader: { ...prev.onlineSpeakersHeader, title: value } 
      }));
    } else if (field === 'text') {
      setSpeakersData(prev => ({ 
        ...prev, 
        onlineSpeakersIntro: { ...prev.onlineSpeakersIntro, text: value } 
      }));
    } else if (field === 'sectionTitle') {
      setSpeakersData(prev => ({ 
        ...prev, 
        onlineSpeakersSection: { ...prev.onlineSpeakersSection, title: value } 
      }));
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Speakers est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* General Settings */}
      <div className="section-header">
        <h3>Paramètres Généraux</h3>
      </div>
      <div className="form-group" style={{ marginBottom: '30px' }}>
        <label>Mode d'affichage des intervenants (Site public) :</label>
        <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
          <button
            className={`btn ${speakersData.displayMode === 'grouped' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSpeakersData(prev => ({ ...prev, displayMode: 'grouped' }))}
            style={{ flex: 1 }}
          >
            Organiser par axes (Cadres)
          </button>
          <button
            className={`btn ${speakersData.displayMode === 'flat' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSpeakersData(prev => ({ ...prev, displayMode: 'flat' }))}
            style={{ flex: 1 }}
          >
            Liste simple (Tous ensemble)
          </button>
          <button
            className={`btn ${speakersData.displayMode === 'by_category' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSpeakersData(prev => ({ ...prev, displayMode: 'by_category' }))}
            style={{ flex: 1 }}
          >
            Organiser par CATÉGORIE
          </button>
          <button
            className={`btn ${speakersData.displayMode === 'speakersgroupedbyLaboratoire' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSpeakersData(prev => ({ ...prev, displayMode: 'speakersgroupedbyLaboratoire' }))}
            style={{ flex: 1 }}
          >
            Organiser par Laboratoire
          </button>
        </div>
        <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
          Ce paramètre détermine comment la liste des intervenants est affichée sur la page principale lorsque aucune conférence n'est sélectionnée.
        </small>
        
        {speakersData.displayMode === 'by_category' && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f4f8', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ fontSize: '0.9em' }}>Ordre INTERNATIONAUX PRINCIPAUX:</label>
              <input 
                type="number" 
                value={speakersData.categoryInternationalOrder}
                onChange={(e) => setSpeakersData(prev => ({ ...prev, categoryInternationalOrder: parseInt(e.target.value) || 0 }))}
                style={{ width: '50px', marginLeft: '10px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.9em' }}>Ordre MAROCAINS:</label>
              <input 
                type="number" 
                value={speakersData.categoryMarocainOrder}
                onChange={(e) => setSpeakersData(prev => ({ ...prev, categoryMarocainOrder: parseInt(e.target.value) || 0 }))}
                style={{ width: '80px', marginLeft: '10px' }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'regular' ? 'active' : ''}`}
          onClick={() => setActiveTab('regular')}
        >
          Intervenants Présentiels
        </button>
        <button 
          className={`tab-btn ${activeTab === 'online' ? 'active online' : ''}`}
          onClick={() => setActiveTab('online')}
        >
          Intervenants En Ligne
        </button>
      </div>

      {activeTab === 'regular' ? (
        <>
          {/* Regular Speakers Header */}
          <div className="section-header ">
            <h3>En-tête des Intervenants</h3>
          </div>
          <div className="form-group">
            <label>Titre:</label>
            <input 
              type="text" 
              value={speakersData.speakersHeader.title}
              onChange={(e) => handleUpdateRegularSpeakersHeader('title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Texte d'introduction:</label>
            <textarea 
              value={speakersData.speakersIntro.text}
              onChange={(e) => handleUpdateRegularSpeakersHeader('text', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Titre de la section:</label>
            <input 
              type="text" 
              value={speakersData.speakersSection.title}
              onChange={(e) => handleUpdateRegularSpeakersHeader('sectionTitle', e.target.value)}
            />
          </div>
        </>
      ) : (
        <>
          {/* Online Speakers Header */}
          <div className="section-header online-speakers-header-section">
            <h3>En-tête des Intervenants En Ligne</h3>
          </div>
          <div className="form-group">
            <label>Titre:</label>
            <input 
              type="text" 
              value={speakersData.onlineSpeakersHeader.title}
              onChange={(e) => handleUpdateOnlineSpeakersHeader('title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Texte d'introduction:</label>
            <textarea 
              value={speakersData.onlineSpeakersIntro.text}
              onChange={(e) => handleUpdateOnlineSpeakersHeader('text', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Titre de la section:</label>
            <input 
              type="text" 
              value={speakersData.onlineSpeakersSection.title}
              onChange={(e) => handleUpdateOnlineSpeakersHeader('sectionTitle', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Speakers Management */}
      <div className={`section-header ${activeTab === 'online' ? 'online-section' : ''}`}>
        <h3>Gestion des {activeTab === 'regular' ? 'Intervenants' : 'Intervenants En Ligne'}</h3>
        <button className="add-btn" onClick={handleAddSpeaker}>
          <HiPlus /> Ajouter un intervenant
        </button>
      </div>

      <div className="card">
        <DataTable 
          value={activeTab === 'regular' ? speakersData.speakers : speakersData.onlineSpeakers} 
          rowGroupMode="subheader" 
          groupRowsBy="mainCategory"
          sortMode="single" 
          sortField="displayOrder" 
          sortOrder={1}
          responsiveLayout="scroll" 
          scrollable
          scrollHeight="flex"
          tableStyle={{ tableLayout: 'auto' }}
          breakpoint="960px"
          rowGroupHeaderTemplate={(data) => (
            <div className="flex align-items-center gap-2" style={{ 
                backgroundColor: '#f1f5f9', 
                padding: '12px 20px', 
                fontWeight: 'bold', 
                fontSize: '0.95em',
                color: '#475569',
                borderLeft: `5px solid ${data.mainCategory === 'marocain' ? '#047857' : '#002b5b'}`
            }}>
                <span>{data.mainCategory === 'marocain' ? 'INTERVENANTS INTERNATIONAUX MAROCAINS' : 'INTERVENANTS INTERNATIONAUX PRINCIPAUX'}</span>
            </div>
          )}
          globalFilter={searchQuery}
          header={
            <div className="flex justify-content-between align-items-center py-2">
              <span className="p-input-icon-left w-full sm:w-20rem">
                <i className="pi pi-search" style={{ left: '1rem' }} />
                <InputText 
                  type="search" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder="Rechercher par nom, titre ou organisation..." 
                  className="w-full"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </span>
            </div>
          }
          className="p-datatable-sm"
          stripedRows
          showGridlines
          emptyMessage="Aucun intervenant trouvé."
        >
          <Column 
            header="Ordre" 
            style={{ width: '1%' }} 
            body={(rowData) => {
                // Find index in filtered list or original list? 
                // DataTable handles sorting. We want to show the controls if not filtered.
                const list = activeTab === 'regular' ? speakersData.speakers : speakersData.onlineSpeakers;
                const index = list.findIndex(s => s.id === rowData.id);
                const categoryList = list.filter(s => (s.mainCategory || 'international') === (rowData.mainCategory || 'international'));
                const indexInCategory = categoryList.findIndex(s => s.id === rowData.id);

                return (
                    <div className="flex align-items-center gap-2">
                      <InputText 
                        value={rowData.displayOrder ?? index} 
                        onChange={async (e) => {
                          const newOrder = parseInt(e.target.value) || 0;
                          const listType = activeTab === 'regular' ? 'speakers' : 'onlineSpeakers';
                          try {
                            await speakersService.updateSpeakerOrder(rowData.id, newOrder);
                            setSpeakersData(prev => ({
                              ...prev,
                              [listType]: prev[listType].map(s => s.id === rowData.id ? { ...s, displayOrder: newOrder } : s)
                            }));
                          } catch (err) { console.error(err); }
                        }}
                        size="small"
                        style={{ width: '50px', transform: 'scale(0.85)' }}
                      />
                      <div className="flex flex-column gap-1">
                        <Button 
                          icon="pi pi-chevron-up" 
                          onClick={() => handleMoveUp(rowData.id)}
                          disabled={indexInCategory === 0 || searchQuery.trim() !== ''} 
                          className="p-button-text p-button-sm p-0"
                          style={{ height: '20px', width: '20px' }}
                        />
                        <Button 
                          icon="pi pi-chevron-down" 
                          onClick={() => handleMoveDown(rowData.id)}
                          disabled={indexInCategory === categoryList.length - 1 || searchQuery.trim() !== ''}
                          className="p-button-text p-button-sm p-0"
                          style={{ height: '20px', width: '20px' }}
                        />
                      </div>
                    </div>
                );
            }} 
          />
          <Column 
            header="Aperçu" 
            body={(rowData) => (
                <Image 
                    src={rowData.image} 
                    alt={rowData.name} 
                    width="45" 
                    preview 
                    className="border-circle" 
                    imageClassName="border-circle"
                    style={{ borderRadius: '50%', overflow: 'hidden' }}
                />
            )} 
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column field="name" header="Nom" sortable filter filterPlaceholder="Filtrer par nom" style={{ whiteSpace: 'nowrap' }} />
          <Column 
            field="title" 
            header="Titre" 
            sortable 
            body={(rowData) => (
                <ExpandableText text={rowData.title} maxChars={60} />
            )}
            style={{ minWidth: '250px', whiteSpace: 'normal' }}
          />
          <Column 
            field="organization" 
            header="Organisation" 
            sortable 
            body={(rowData) => (
                <ExpandableText text={rowData.organization} maxChars={70} />
            )}
            style={{ minWidth: '300px', whiteSpace: 'normal' }}
          />
          <Column 
            field="mainCategory" 
            header="Catégorie" 
            sortable 
            body={(rowData) => (
                <Tag 
                    value={rowData.mainCategory === 'marocain' ? 'MAROCAIN' : 'INTERNATIONAL'} 
                    severity={rowData.mainCategory === 'marocain' ? 'success' : 'danger'} 
                />
            )}
            style={{ whiteSpace: 'nowrap', width: '1%' }}
          />
          <Column field="cadreTitle" header="Cadres" body={(rowData) => rowData.cadreTitle || 'Aucun'} style={{ whiteSpace: 'nowrap' }} />
          <Column 
            header="Actions" 
            body={(rowData) => (
                <div className="flex gap-2">
                    <Button 
                        icon="pi pi-pencil" 
                        rounded 
                        outlined 
                        severity="info" 
                        onClick={() => handleEditSpeaker(rowData)} 
                    />
                    <Button 
                        icon="pi pi-trash" 
                        rounded 
                        outlined 
                        severity="danger" 
                        onClick={() => handleDeleteSpeaker(rowData.id)} 
                    />
                </div>
            )} 
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>

      {/* Speaker Modal */}
      {showModal && (
        <SpeakerModal
          mode={modalMode}
          speaker={selectedSpeaker}
          activeTab={activeTab}
          cadresList={cadresList}
          onSave={async (speakerData) => {
            try {
              if (modalMode === 'add') {
                const listType = activeTab === 'regular' ? 'speakers' : 'onlineSpeakers';
                const currentSpeakers = speakersData[listType] || [];
                const maxOrder = currentSpeakers.reduce((max, s) => Math.max(max, s.displayOrder || 0), -1);
                
                const newSpeakerData = { 
                  ...speakerData, 
                  displayOrder: maxOrder + 1 
                };

                const newSpeaker = await speakersService.addSpeaker(newSpeakerData, selectedEdition, activeTab);
                const formattedSpeaker = {
                  id: newSpeaker.id,
                  name: newSpeaker.name,
                  title: newSpeaker.title,
                  organization: newSpeaker.organization,
                  image: newSpeaker.image_url,
                  imagePath: newSpeaker.image_path,
                  companyLogo: newSpeaker.company_logo_url,
                  companyLogoPath: newSpeaker.company_logo_path,
                  country: newSpeaker.country,
                  flagUrl: newSpeaker.flag_url,
                  flagPath: newSpeaker.flag_path,
                  mainCategory: newSpeaker.main_category,
                  displayOrder: newSpeaker.display_order
                };
                
                setSpeakersData(prev => ({
                  ...prev,
                  [listType]: [
                    ...prev[listType],
                    formattedSpeaker
                  ]
                }));
              } else {
                await speakersService.updateSpeaker(selectedSpeaker.id, speakerData);
                setSpeakersData(prev => ({
                  ...prev,
                  speakers: prev.speakers.map(speaker =>
                    speaker.id === selectedSpeaker.id ? { ...speaker, ...speakerData } : speaker
                  ),
                  onlineSpeakers: prev.onlineSpeakers.map(speaker =>
                    speaker.id === selectedSpeaker.id ? { ...speaker, ...speakerData } : speaker
                  )
                }));
              }
              showSuccessMessage();
              closeModal();
            } catch (error) {
              console.error('Error saving speaker:', error);
              alert('Erreur lors de la sauvegarde de l\'intervenant.');
            }
          }}
          onClose={closeModal}
          selectedEdition={selectedEdition}
        />
      )}
    </div>
  );
}

function SpeakerModal({ mode, speaker, activeTab, cadresList, onSave, onClose, selectedEdition }) {
  const [formData, setFormData] = useState({
    name: speaker?.name || '',
    title: speaker?.title || '',
    organization: speaker?.organization || '',
    image: speaker?.image || '../images/profile2.png',
    imagePath: speaker?.imagePath || '',
    companyLogo: speaker?.companyLogo || '../images/company.png',
    companyLogoPath: speaker?.companyLogoPath || '',
    country: speaker?.country || 'US',
    flagUrl: speaker?.flagUrl || 'https://flagcdn.com/w320/us.png',
    flagPath: speaker?.flagPath || '',
    mainCategory: speaker?.mainCategory || 'international',
    cadreIds: speaker?.cadreIds || []
  });
  const [imagePreview, setImagePreview] = useState(speaker?.image || '../images/profile2.png');
  const [companyLogoPreview, setCompanyLogoPreview] = useState(speaker?.companyLogo || '../images/company.png');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [personSearch, setPersonSearch] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [isSearchingPersons, setIsSearchingPersons] = useState(false);

  const handlePersonSearch = async (query) => {
    setPersonSearch(query);
    if (query.length < 2) {
      setPersonResults([]);
      return;
    }
    try {
      setIsSearchingPersons(true);
      const results = await speakersService.getPersons(query);
      setPersonResults(results);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingPersons(false);
    }
  };

  const selectPerson = (person) => {
    setFormData({
      ...formData,
      person_id: person.id,
      name: person.name,
      title: person.title || '',
      organization: person.organization || '',
      image: person.image_url || '../images/profile2.png',
      imagePath: person.image_path || '',
      companyLogo: person.company_logo_url || '../images/company.png',
      companyLogoPath: person.company_logo_path || '',
      country: person.country || 'US',
      flagUrl: person.flag_url || 'https://flagcdn.com/w320/us.png',
      flagPath: person.flag_path || ''
    });
    setImagePreview(person.image_url || '../images/profile2.png');
    setCompanyLogoPreview(person.company_logo_url || '../images/company.png');
    setPersonResults([]);
    setPersonSearch('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop volumineuse. Taille maximum: 5MB.');
      return;
    }

    try {
      setUploadingImage(true);

      // Delete old image if exists
      if (formData.imagePath) {
        await speakersService.deleteSpeakerFile(formData.imagePath);
      }

      // Upload new image
      const uploadResult = await speakersService.uploadSpeakerImage(file, selectedEdition);
      
      setFormData(prev => ({ 
        ...prev, 
        image: uploadResult.url,
        imagePath: uploadResult.path
      }));
      setImagePreview(uploadResult.url);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors du téléchargement de l\'image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCompanyLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop volumineuse. Taille maximum: 5MB.');
      return;
    }

    try {
      setUploadingLogo(true);

      // Delete old logo if exists
      if (formData.companyLogoPath) {
        await speakersService.deleteSpeakerFile(formData.companyLogoPath);
      }

      // Upload new logo
      const uploadResult = await speakersService.uploadCompanyLogo(file, selectedEdition);
      
      setFormData(prev => ({ 
        ...prev, 
        companyLogo: uploadResult.url,
        companyLogoPath: uploadResult.path
      }));
      setCompanyLogoPreview(uploadResult.url);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Erreur lors du téléchargement du logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCountryChange = (e) => {
    const selectedCountry = countries.find(country => country.code === e.target.value);
    setFormData({
      ...formData,
      country: selectedCountry.code,
      flagUrl: selectedCountry.flag
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const speakerData = mode === 'edit' ? { ...formData, id: speaker.id } : formData;
    onSave(speakerData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter un Intervenant' : 'Modifier l\'Intervenant'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        
        <div className="person-search-section" style={{ padding: '20px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #eee' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
            Rechercher un intervenant existant (évite les doublons) :
          </label>
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Tapez un nom..." 
              value={personSearch}
              onChange={(e) => handlePersonSearch(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
            {isSearchingPersons && <div style={{ position: 'absolute', right: '10px', top: '10px' }}>Chargement...</div>}
            
            {personResults.length > 0 && (
              <div style={{ 
                position: 'absolute', top: '100%', left: 0, width: '100%', 
                backgroundColor: 'white', border: '1px solid #ddd', zIndex: 10,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto'
              }}>
                {personResults.map(p => (
                  <div 
                    key={p.id} 
                    onClick={() => selectPerson(p)}
                    style={{ padding: '10px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                    onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseOut={e => e.target.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: 'bold' }}>{p.name}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>{p.title} - {p.organization}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Nom complet</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Entrez le nom complet"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Titre/Spécialité</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Entrez le titre ou spécialité"
                  // required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Organisation/Institution</label>
              <input 
                type="text" 
                value={formData.organization}
                onChange={(e) => setFormData({...formData, organization: e.target.value})}
                placeholder="Entrez l'organisation"
                // required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Pays</label>
                <select 
                  value={formData.country}
                  onChange={handleCountryChange}
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <div className="flag-preview">
                  <img 
                    src={formData.flagUrl} 
                    alt="Flag" 
                    style={{ width: '40px', height: '24px', marginTop: '8px', borderRadius: '2px' }}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Catégorie (Intervenant Principal)</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="mainCategory" 
                    value="international" 
                    checked={formData.mainCategory === 'international'}
                    onChange={(e) => setFormData({...formData, mainCategory: e.target.value})}
                  />
                  INTERNATIONAL
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="mainCategory" 
                    value="marocain" 
                    checked={formData.mainCategory === 'marocain'}
                    onChange={(e) => setFormData({...formData, mainCategory: e.target.value})}
                  />
                  MAROCAIN
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Cadres de conférence</label>
              <select
                multiple
                value={formData.cadreIds}
                onChange={e => {
                  const options = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setFormData({ ...formData, cadreIds: options });
                }}
                style={{ minHeight: '60px' }}
              >
                {cadresList.map(cadre => (
                  <option key={cadre.id} value={cadre.id}>
                    {cadre.title}
                  </option>
                ))}
              </select>
              <small>Maintenez Ctrl (Cmd sur Mac) pour sélectionner plusieurs cadres.</small>
            </div>

            <div className="form-group">
              <label>Photo de profil</label>
              <div className="image-upload-container">
                <div className="image-preview-container">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="image-preview"
                    style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                </div>
                <div className="upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="image-upload"
                    className="file-input"
                    disabled={uploadingImage}
                  />
                  <label htmlFor="image-upload" className={`upload-btn ${uploadingImage ? 'uploading' : ''}`}>
                    <HiOutlinePhotograph />
                    {uploadingImage ? 'Téléchargement...' : 'Choisir une image de profil'}
                  </label>
                  <p className="upload-hint">JPG, PNG ou GIF (max. 5MB)</p>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Logo de l'organisation</label>
              <div className="image-upload-container">
                <div className="image-preview-container">
                  <img 
                    src={companyLogoPreview} 
                    alt="Company Logo Preview" 
                    className="image-preview"
                    style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                  />
                </div>
                <div className="upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleCompanyLogoUpload}
                    id="company-logo-upload"
                    className="file-input"
                    disabled={uploadingLogo}
                  />
                  <label htmlFor="company-logo-upload" className={`upload-btn ${uploadingLogo ? 'uploading' : ''}`}>
                    <HiOutlinePhotograph />
                    {uploadingLogo ? 'Téléchargement...' : 'Choisir un logo'}
                  </label>
                  <p className="upload-hint">JPG, PNG ou GIF (max. 5MB)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'add' ? 'Ajouter' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SpeakersManagement;

